// Keeps the state of a result list (loaded pages, scroll position, view options) in
// sessionStorage so that returning to the list - e.g. after opening an offer, which is a
// full page navigation away from the SPA - lands the user where they left off.
//
// The loaded pages are stored under their own key: they are large, while the scroll
// position is rewritten constantly while scrolling.

const STATE_PREFIX = "listState:";
const PAGES_PREFIX = "listPages:";
const TTL_MS = 30 * 60 * 1000;

export interface ListState {
  savedAt: number;
  scrollY?: number;
  viewMode?: "list" | "grid" | "map";
  minRelevance80?: boolean;
  mapView?: { lng: number; lat: number; zoom: number };
}

export interface ListPages {
  savedAt: number;
  pages: any[];
  pageParams: any[];
}

function getStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function read<T extends { savedAt: number }>(storageKey: string): T | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as T;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > TTL_MS) {
      storage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Only the list the user is currently on is worth keeping; dropping the others keeps the
// stored pages from filling up the storage quota.
function removeOthers(storage: Storage, prefix: string, keep: string): void {
  for (let i = storage.length - 1; i >= 0; i--) {
    const existing = storage.key(i);
    if (existing && existing.startsWith(prefix) && existing !== keep) {
      storage.removeItem(existing);
    }
  }
}

/** Forgets every stored list: they belong to whoever was signed in. */
export function clearListCache(): void {
  const storage = getStorage();
  if (!storage) return;
  for (let i = storage.length - 1; i >= 0; i--) {
    const existing = storage.key(i);
    if (existing && (existing.startsWith(STATE_PREFIX) || existing.startsWith(PAGES_PREFIX))) {
      storage.removeItem(existing);
    }
  }
}

export function readListState(key: string): ListState | null {
  return read<ListState>(STATE_PREFIX + key);
}

export function writeListState(key: string, patch: Partial<ListState>): void {
  const storage = getStorage();
  if (!storage) return;
  const storageKey = STATE_PREFIX + key;
  const next: ListState = { ...(readListState(key) ?? {}), ...patch, savedAt: Date.now() };
  removeOthers(storage, STATE_PREFIX, storageKey);
  try {
    storage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // storage full or unavailable - the list still works, it just won't be restored
  }
}

export function readListPages(key: string): ListPages | null {
  return read<ListPages>(PAGES_PREFIX + key);
}

export function writeListPages(key: string, pages: any[], pageParams: any[]): void {
  const storage = getStorage();
  if (!storage) return;
  const storageKey = PAGES_PREFIX + key;
  removeOthers(storage, PAGES_PREFIX, storageKey);
  try {
    storage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), pages, pageParams }));
  } catch {
    // Too big for the quota - drop what we can't keep so a stale half-list isn't restored.
    storage.removeItem(storageKey);
  }
}
